/**
 * SearchResultsPage — full-page search at `/food-pulse/search?q=<query>`.
 *
 * Tabs across the top: All · Products · Restaurants · Articles · Recipes ·
 * Deals · Places · News.  Clicking a tab filters to that group; "All" shows
 * everything grouped in one long list.
 *
 * Uses the same `/api/search` endpoint as the omnibar, but with a higher
 * `per_type=30` so the tabs actually surface enough rows to browse.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Search, Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import SeoHead from "@/components/SeoHead";

const API   = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

const GROUP_ICON = {
  food_item: "🥬", food_business: "🏬", food_article: "📰",
  recipe: "🍳", deal: "🏷️", place: "📍", news: "📄",
};

// Tabs shown in the pill bar. Keep in the same order as the backend's
// GROUP_ORDER so tab labels line up with the response groups.
const TABS = [
  { key: "all",           label: "All" },
  { key: "food_item",     label: "Products" },
  { key: "food_business", label: "Restaurants & Vendors" },
  { key: "food_article",  label: "Articles" },
  { key: "recipe",        label: "Recipes" },
  { key: "deal",          label: "Deals" },
  { key: "place",         label: "Places" },
  { key: "news",          label: "News" },
];

function HitCard({ hit }) {
  return (
    <Link to={hit.url}
          className="flex items-center gap-3 rounded-2xl border bg-white p-3 hover:shadow-md transition-all hover:-translate-y-0.5"
          style={{ borderColor: "#E4DDD1" }}
          data-testid={`search-hit-${hit.type}-${hit.id}`}>
      {hit.image ? (
        <img src={hit.image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl" style={{ background: "#F5F0E4" }}>
          {GROUP_ICON[hit.type] || "•"}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-display text-base truncate" style={{ color: GREEN }}>{hit.title}</div>
        {hit.subtitle && <div className="text-xs opacity-70 truncate">{hit.subtitle}</div>}
        {hit.snippet && <div className="text-[11px] opacity-60 mt-0.5 line-clamp-2">{hit.snippet}</div>}
      </div>
    </Link>
  );
}

export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const initialQ    = params.get("q") || "";
  const initialTab  = params.get("tab") || "all";
  const [q, setQ]       = useState(initialQ);
  const [tab, setTab]   = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Only refetch when the actual query text in the URL changes — NOT when
  // the user just switches tabs (the whole payload has all groups already).
  const urlQ = params.get("q") || "";
  useEffect(() => {
    if (urlQ.trim().length < 2) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/search`, {
      params: { q: urlQ, per_type: 30 },
    })
    .then((r) => setData(r.data))
    .catch(() => setData({ groups: [], total: 0 }))
    .finally(() => setLoading(false));
  }, [urlQ]);

  // Keep local tab state synced with the URL tab param when the URL changes.
  useEffect(() => {
    setTab(params.get("tab") || "all");
  }, [params]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    const next = new URLSearchParams();
    next.set("q", q.trim());
    if (tab !== "all") next.set("tab", tab);
    setParams(next);
  };

  const setTabAndUrl = (key) => {
    setTab(key);
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("tab"); else next.set("tab", key);
    setParams(next);
  };

  // Group counts across all groups
  const groups = data?.groups || [];
  const counts = { all: data?.total || 0 };
  groups.forEach((g) => { counts[g.type] = g.count; });

  const visibleGroups = tab === "all" ? groups : groups.filter((g) => g.type === tab);

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="search-results-page">
      <SeoHead section="food-pulse-search" title={q ? `Search: ${q} — Food Pulse` : "Search — Food Pulse"} description="Search products, restaurants, recipes, articles and deals across FoodPulse." />

      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs mb-4 opacity-70 hover:opacity-100">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <h1 className="font-display text-3xl sm:text-4xl mb-4" style={{ color: GREEN }}>
          {initialQ ? <>Results for "<span style={{ color: "#7C3AED" }}>{initialQ}</span>"</> : "Search"}
        </h1>

        {/* Search bar */}
        <form onSubmit={submitSearch} className="relative mb-6 max-w-2xl">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 type="search" placeholder="Search products, restaurants, recipes…"
                 className="w-full rounded-full border pl-11 pr-4 py-3 text-base bg-white outline-none focus:ring-2 focus:ring-amber-300"
                 style={{ borderColor: "#E4DDD1" }}
                 data-testid="search-page-input" />
        </form>

        {/* Grouping tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="search-tabs">
          {TABS.map(({ key, label }) => {
            const n = counts[key];
            if (key !== "all" && !n) return null;
            const active = tab === key;
            return (
              <button key={key} onClick={() => setTabAndUrl(key)}
                      className={`text-sm font-semibold px-4 py-2 rounded-full border transition ${active ? "text-white" : "opacity-80 hover:opacity-100"}`}
                      style={{
                        background: active ? GREEN : "#fff",
                        color: active ? "#fff" : GREEN,
                        borderColor: active ? GREEN : "#E4DDD1",
                      }}
                      data-testid={`search-tab-${key}`}>
                {label}{n != null ? ` (${n})` : ""}
              </button>
            );
          })}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm opacity-70" data-testid="search-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching…
          </div>
        ) : !data || data.total === 0 ? (
          <div className="rounded-2xl border-2 border-dashed p-10 text-center bg-white" data-testid="search-empty" style={{ borderColor: "#B8D3A1" }}>
            <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div className="font-display text-lg mb-1">No matches yet.</div>
            <div className="text-sm opacity-70">Try a different word or check the spelling.</div>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed p-8 text-center bg-white" style={{ borderColor: "#B8D3A1" }}>
            <div className="text-sm opacity-70">No matches in this category. Try a different tab.</div>
          </div>
        ) : (
          <div className="space-y-8">
            {visibleGroups.map((g) => (
              <section key={g.type} data-testid={`search-section-${g.type}`}>
                <h2 className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-3 flex items-center gap-1.5">
                  <span>{GROUP_ICON[g.type] || "•"}</span> {g.label} · {g.count}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {g.items.map((hit) => <HitCard key={`${hit.type}-${hit.id}`} hit={hit} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
